/**
 * One trader, and everything their order path is allowed to do — ROADMAP 9.12.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BOUND SITS BESIDE THE CAPABILITY IT BOUNDS
 *
 * Slicing and its ceiling are one decision read together: slicing on with no
 * ceiling is the fat-fingered-extra-zero case its own schema comment warns
 * about, and the admin has to be able to see both without moving their eyes.
 * Same for auto-price and its time bound, which is the difference between
 * chasing an order and following a running market until the close.
 *
 * So the columns pair up — capability, then its bound — rather than grouping
 * all three switches and then all three numbers.
 *
 * A VIEWER'S CAPABILITIES ARE SHOWN AND DISABLED, not hidden. A viewer places
 * nothing (the order door checks the role before the RMS gates), so granting
 * them slicing is meaningless — but a blank row invites the question "is this
 * screen broken", and the honest answer is on the control itself.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { AlertTriangle, Loader2 } from 'lucide-react'
import {
  DEFAULT_MAX_SLICE_ORDERS,
  EXECUTION_POLICY_LIMITS,
  MAX_LIMIT_BUFFER_PCT,
  type ExecutionPolicyPatch,
  type TraderPolicyDto,
} from '@apna/shared'
import { cn } from '@/lib/utils'
import { CapabilityToggle } from './CapabilityToggle'
import { LimitCell } from './LimitCell'

const ago = (iso: string | null): string => {
  if (iso === null) return 'never'
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

const units = (v: number): string => (v === 0 ? '—' : v.toLocaleString('en-IN'))
const ms = (v: number): string => (v === 0 ? '—' : `${(v / 1000).toLocaleString('en-IN')}s`)
const pct = (v: number): string => `${(v * 100).toFixed(2)}%`
/** 0 reads as the compiled-in default rather than as "no orders allowed". */
const orders = (v: number): string => (v === 0 ? `${DEFAULT_MAX_SLICE_ORDERS}*` : String(v))

/**
 * Roughly how long releasing that many orders takes, for the tooltip.
 *
 * `burst: 4, perSec: 5` is the rate budget, chosen so `burst + perSec` stays
 * under SEBI's threshold of 10/sec. This is the cost of a big slice count and
 * the admin should see it on the control that sets it: the bound is SECONDS
 * IN THE MARKET, not anything on our servers.
 */
const releaseSeconds = (count: number): number => Math.max(0, Math.ceil((count - 4) / 5))

export function TraderRow({
  trader,
  isSelf,
  selected,
  onSelect,
  busy,
  refusal,
  onChange,
}: {
  trader: TraderPolicyDto
  isSelf: boolean
  selected: boolean
  onSelect: (next: boolean) => void
  busy: boolean
  refusal: string | undefined
  onChange: (patch: ExecutionPolicyPatch) => void
}) {
  const p = trader.policy
  const viewer = trader.role === 'VIEWER'
  const locked = viewer ? 'A viewer places no orders, so this changes nothing for them.' : undefined

  return (
    <>
      <tr
        data-testid={`trader-row-${trader.userId}`}
        className={cn('border-b border-border/60 last:border-0', selected && 'bg-primary/[0.04]')}
      >
        <td className="py-2 pl-3 pr-1 align-middle">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
            aria-label={`Select ${trader.email}`}
            data-testid={`select-${trader.userId}`}
            className="h-3.5 w-3.5 cursor-pointer accent-[hsl(var(--primary))]"
          />
        </td>

        {/* `max-w-0` is gone with auto layout: the column has a declared
            width now, so `truncate` has something to truncate against. */}
        <td className="py-2 pr-3 align-middle">
          <div className="flex items-center gap-1.5">
            {/* `min-w-0`: a flex item never shrinks below its own content
                without it, so `truncate` would have nothing to act on. */}
            <span className="min-w-0 truncate text-[12.5px] font-medium" title={trader.email}>
              {trader.name ?? trader.email}
            </span>
            {isSelf ? (
              <span className="shrink-0 rounded bg-secondary px-1 py-px text-[8.5px] font-bold uppercase tracking-wider text-muted-foreground">
                you
              </span>
            ) : null}
          </div>
          {trader.name === null ? null : (
            <span className="block truncate text-[10.5px] text-muted-foreground" title={trader.email}>
              {trader.email}
            </span>
          )}
        </td>

        <td className="py-2 pr-3 align-middle text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          {trader.role}
        </td>

        {/* Can they place anything, and are they holding anything. Both decide
            how loud a capability change is — see `bulkWarning`. */}
        <td className="py-2 pr-3 align-middle text-right text-[12px] tabular-nums text-muted-foreground">
          {trader.accounts}
        </td>
        <td className="py-2 pr-4 align-middle text-right text-[12px] tabular-nums">
          {trader.openTrades === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="font-medium text-foreground" title="Open trades. A capability change lands mid-book.">
              {trader.openTrades}
            </span>
          )}
        </td>

        <td className="py-2 pr-2 align-middle">
          <CapabilityToggle
            on={p?.sliceEnabled === true}
            label={`Order slicing for ${trader.email}`}
            busy={busy}
            disabled={viewer}
            hint={locked ?? 'Split one leg into several orders to clear the exchange freeze limit.'}
            testId={`slice-${trader.userId}`}
            onChange={(next) => onChange({ sliceEnabled: next })}
          />
        </td>
        <td className="py-2 pr-4 text-right align-middle">
          <LimitCell
            value={p?.sliceMaxQty ?? 0}
            min={EXECUTION_POLICY_LIMITS.sliceMaxQty.min}
            max={EXECUTION_POLICY_LIMITS.sliceMaxQty.max}
            format={units}
            unsetHint="No ceiling of yours — only the exchange freeze limit applies, and that is per ORDER."
            busy={busy}
            disabled={viewer}
            testId={`slice-max-${trader.userId}`}
            onCommit={(next) => onChange({ sliceMaxQty: next })}
          />
        </td>
        <td className="py-2 pr-4 text-right align-middle">
          <LimitCell
            value={p?.sliceMaxOrders ?? 0}
            min={EXECUTION_POLICY_LIMITS.sliceMaxOrders.min}
            max={EXECUTION_POLICY_LIMITS.sliceMaxOrders.max}
            format={orders}
            /*
             * The hint states the COST, because it is not obvious and it is
             * not server load: orders are paced at 5/sec, so the count buys
             * seconds during which the leg is only partly in the market.
             */
            unsetHint={`${DEFAULT_MAX_SLICE_ORDERS} by default — about ${releaseSeconds(DEFAULT_MAX_SLICE_ORDERS)}s to release one full leg at 5 orders/sec.`}
            busy={busy}
            disabled={viewer}
            testId={`slice-orders-${trader.userId}`}
            onCommit={(next) => onChange({ sliceMaxOrders: next })}
          />
        </td>

        <td className="py-2 pr-2 align-middle">
          <CapabilityToggle
            on={p?.autoChaseEnabled === true}
            label={`Auto-price for ${trader.email}`}
            busy={busy}
            disabled={viewer}
            hint={locked ?? 'Re-price a resting order toward the touch until it fills.'}
            testId={`chase-${trader.userId}`}
            onChange={(next) => onChange({ autoChaseEnabled: next })}
          />
        </td>
        <td className="py-2 pr-4 text-right align-middle">
          <LimitCell
            value={p?.autoChaseMaxMs ?? 0}
            min={EXECUTION_POLICY_LIMITS.autoChaseMaxMs.min}
            max={EXECUTION_POLICY_LIMITS.autoChaseMaxMs.max}
            step={500}
            format={ms}
            unsetHint="The engine's compiled-in bound. A chase is never unbounded."
            busy={busy}
            disabled={viewer}
            testId={`chase-max-${trader.userId}`}
            onCommit={(next) => onChange({ autoChaseMaxMs: next })}
          />
        </td>

        <td className="py-2 pr-4 align-middle">
          <CapabilityToggle
            on={p?.manualNetting === true}
            label={`Manual netting for ${trader.email}`}
            busy={busy}
            disabled={viewer}
            hint={locked ?? 'A chain click on a held strike reduces that manual leg instead of opening a second one.'}
            testId={`netting-${trader.userId}`}
            onChange={(next) => onChange({ manualNetting: next })}
          />
        </td>

        <td className="py-2 pr-3 text-right align-middle">
          <LimitCell
            value={p?.limitBufferPct ?? 0.003}
            min={EXECUTION_POLICY_LIMITS.limitBufferPct.min}
            max={EXECUTION_POLICY_LIMITS.limitBufferPct.max}
            step={0.001}
            format={pct}
            unsetHint={`How far through the touch a market-like order is priced. At most ${(MAX_LIMIT_BUFFER_PCT * 100).toFixed(0)}%.`}
            busy={busy}
            disabled={viewer}
            testId={`buffer-${trader.userId}`}
            onCommit={(next) => onChange({ limitBufferPct: next })}
          />
        </td>

        <td className="py-2 pr-3 align-middle text-right text-[10.5px] text-muted-foreground">
          {busy ? (
            <Loader2 size={12} className="ml-auto animate-spin" />
          ) : p === null ? (
            /* NO ROW is rendered as itself. "Nobody turned this on" and
               "someone turned it on and then off" are different answers, and
               the schema's own header keeps them apart on purpose. */
            <span title="No capability row. Nobody has ever granted this login anything.">no row</span>
          ) : (
            <span title={p.updatedAt ?? undefined}>{ago(p.updatedAt)}</span>
          )}
          <span className="sr-only">{`Last active ${ago(trader.lastActiveAt)}`}</span>
        </td>
      </tr>

      {refusal === undefined ? null : (
        <tr className="border-b border-border/60">
          <td />
          <td colSpan={11} className="pb-2 pr-3">
            <p
              role="alert"
              className="flex items-start gap-1.5 text-[11.5px] text-destructive"
              data-testid={`refusal-${trader.userId}`}
            >
              <AlertTriangle size={12} className="mt-px shrink-0" />
              {refusal}
            </p>
          </td>
        </tr>
      )}
    </>
  )
}
